import React, { ReactNode, useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import format from 'date-fns/format';
import { addMinutes, startOfDay } from 'date-fns';

import {
  Paper,
  Box,
  Tooltip,
  SxProps,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  SpotPriceLevel,
  SpotPricePeriod,
} from '../../../core/src/shared/enums';

import { Row } from '../atoms/row';
import { useSelector } from '../store/utils';

const summaryHead = Array.from({ length: 24 }, (_, i) => i + 1);

type Props = {
  plugIndex: number;
};

export const PlugSummary = ({ plugIndex }: Props) => {
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const sm = useMediaQuery(theme.breakpoints.up('sm'));

  const headSx: SxProps = {
    gridColumn: { xs: 'span 96', lg: 'auto' },
    fontSize: { xs: 12, lg: 14 },
    lineHeight: { xs: '12px', lg: '24px' },
    mt: { xs: 1.25, lg: 0 },
    mb: { xs: 0.5, lg: 0 },
    pr: { xs: 0, lg: 1 },
    width: 'auto',
  };

  const [
    prices,
    priceLevels,
    spotEnabled,
    spotLow,
    spotMid,
    spotHi,
    spotNeg,
    spotBelow,
    spotAbove,
    priceBelowValue,
    priceAboveValue,
    plugPricePeriod,
    spPricePeriod,
    spotEm,
    spotOverflow,
  ] = useSelector(
    ({
      variables: {
        SP_HOURS_PRICE,
        SP_PRICE_LEVEL,
        PL_SPOT,
        PL_SPOT_LOW,
        PL_SPOT_MI,
        PL_SPOT_HI,
        PL_SPOT_NEG,
        PL_SPOT_BELOW_PLUG,
        PL_SPOT_ABOVE_PLUG,
        SP_PRICE_BELOW_PLUG_VALUE,
        SP_PRICE_ABOVE_PLUG_VALUE,
        PL_SP_PRICE_PERIOD,
        SP_PRICE_PERIOD,
        SP_EM,
        SP_OVERFLOW_TABLE,
      },
    }) =>
      [
        SP_HOURS_PRICE,
        SP_PRICE_LEVEL,
        PL_SPOT,
        PL_SPOT_LOW,
        PL_SPOT_MI,
        PL_SPOT_HI,
        PL_SPOT_NEG,
        PL_SPOT_BELOW_PLUG,
        PL_SPOT_ABOVE_PLUG,
        SP_PRICE_BELOW_PLUG_VALUE,
        SP_PRICE_ABOVE_PLUG_VALUE,
        PL_SP_PRICE_PERIOD[plugIndex],
        SP_PRICE_PERIOD,
        SP_EM,
        SP_OVERFLOW_TABLE,
      ] as const
  );

  // Simple plan for 60M spot control for selected plug
  const schedule = useMemo(() => {
    const result: boolean[] = [];
    const isSpotOn = spotEnabled[plugIndex];

    for (let hour = 0; hour < 24; hour++) {
      let on = false;
      if (!isSpotOn) {
        result.push(false);
        continue;
      }

      const level = priceLevels[hour];
      const price = prices[hour];

      if (spotLow[plugIndex] && level === SpotPriceLevel.Low) on = true;
      if (spotMid[plugIndex] && level === SpotPriceLevel.Mid) on = true;
      if (spotHi[plugIndex] && level === SpotPriceLevel.Hi) on = true;

      // Custom below / above price
      if (
        spotBelow[plugIndex] &&
        priceBelowValue[plugIndex] != null &&
        price <= priceBelowValue[plugIndex]
      ) {
        on = true;
      }
      if (
        spotAbove[plugIndex] &&
        priceAboveValue[plugIndex] != null &&
        price >= priceAboveValue[plugIndex]
      ) {
        on = true;
      }

      // Negative price – follow overflow 60m table across the day
      if (
        spotNeg[plugIndex] &&
        Array.isArray(spotOverflow) &&
        !spotOverflow[hour]
      ) {
        on = true;
      }

      result.push(on);
    }

    return result;
  }, [
    plugIndex,
    prices,
    priceLevels,
    spotEnabled,
    spotLow,
    spotMid,
    spotHi,
    spotNeg,
    spotBelow,
    spotAbove,
    priceBelowValue,
    priceAboveValue,
    spotEm,
    spotOverflow,
  ]);

  if (!prices || prices.length !== 24) return null;
  if (
    plugPricePeriod !== SpotPricePeriod['60M'] &&
    spPricePeriod !== SpotPricePeriod['60M']
  )
    return null;
  if (!spotEnabled[plugIndex]) return null;

  return (
    <Paper sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(96, 1fr)',
            lg: 'auto repeat(96, 1fr)',
          },
          rowGap: { lg: 1 },
        }}
      >
        {/* Čas */}
        <Box sx={{ ...headSx }}>
          <FormattedMessage id="plug.summary.time" defaultMessage="Čas" />
        </Box>
        {summaryHead.map((i) => {
          if (!lg && i % 2 === 0) return null;
          if (!sm && i % 3 === 0) return null;

          return (
            <Box
              component="span"
              key={i}
              sx={{
                textAlign: 'left',
                fontSize: 12,
                gridColumn: { xs: 'span 12', sm: 'span 8', lg: 'span 4' },
                width: 'auto',
              }}
            >
              {i - 1}:00
            </Box>
          );
        })}

        {/* Cena */}
        <Box sx={{ ...headSx }}>
          <FormattedMessage id="plug.summary.price" defaultMessage="Cena" />
        </Box>
        {prices.map((value, i) => {
          if (!lg && (i + 1) % 2 === 0) return null;
          if (!sm && (i + 1) % 3 === 0) return null;

          return (
            <Box
              key={i}
              component="span"
              sx={{
                fontSize: 12,
                gridColumn: { xs: 'span 12', sm: 'span 8', lg: 'span 4' },
                width: 'auto',
              }}
            >
              <FormattedNumber
                value={value}
                maximumFractionDigits={2}
                minimumFractionDigits={2}
              />
            </Box>
          );
        })}

        {/* Hladina ceny */}
        <RowHead sx={headSx}>
          <FormattedMessage
            id="plug.summary.price_level"
            defaultMessage="Hladina ceny"
          />
        </RowHead>
        {priceLevels.map((value, i) => (
          <ColPriceLevel value={value} key={i} periodIndex={i} />
        ))}

        {/* Sepnutí zásuvky */}
        <RowHead sx={headSx}>
          <FormattedMessage
            id="plug.summary.switch"
            defaultMessage="Sepnutí zásuvky"
          />
        </RowHead>
        {schedule.map((value, i) => (
          <ColBoolean
            key={i}
            value={value}
            periodIndex={i}
            pricePeriod={SpotPricePeriod['60M']}
          />
        ))}
      </Box>
    </Paper>
  );
};

const RowHead = ({ children, sx }: { children: ReactNode; sx: SxProps }) => (
  <Box sx={{ ...sx }}>{children}</Box>
);

const ColBoolean = ({
  pricePeriod = SpotPricePeriod['60M'],
  periodIndex,
  value,
}: {
  pricePeriod?: SpotPricePeriod;
  periodIndex: number;
  value: boolean;
}) => {
  return (
    <Tooltip
      title={
        <FormattedPriceIndex index={periodIndex} pricePeriod={pricePeriod} />
      }
    >
      <Box
        sx={{
          width: 'auto',
          height: 26,
          bgcolor: value ? 'primary.main' : 'grey.200',
          gridColumn: pricePeriod === '15M' ? 'span 1' : 'span 4',
        }}
      />
    </Tooltip>
  );
};

const ColPriceLevel = ({
  periodIndex,
  pricePeriod = SpotPricePeriod['60M'],
  value,
}: {
  periodIndex: number;
  pricePeriod?: SpotPricePeriod;
  value: SpotPriceLevel;
}) => {
  let title: ReactNode = null;
  let bgcolor = 'grey.200';

  if (value === SpotPriceLevel.Hi) {
    title = (
      <FormattedMessage
        id="spot.summary.price_level.hi"
        defaultMessage="Vysoká cena"
      />
    );
    bgcolor = 'error.main';
  } else if (value === SpotPriceLevel.Mid) {
    title = (
      <FormattedMessage
        id="spot.summary.price_level.mid"
        defaultMessage="Střední cena"
      />
    );
    bgcolor = '#ff9800';
  } else {
    title = (
      <FormattedMessage
        id="spot.summary.price_level.low"
        defaultMessage="Nízká cena"
      />
    );
    bgcolor = 'success.main';
  }

  return (
    <Tooltip
      title={
        <>
          {title} (
          <FormattedPriceIndex index={periodIndex} pricePeriod={pricePeriod} />)
        </>
      }
    >
      <Box
        sx={{
          width: 'auto',
          height: 26,
          bgcolor,
          gridColumn: pricePeriod === '15M' ? 'span 1' : 'span 4',
        }}
      />
    </Tooltip>
  );
};

const FormattedPriceIndex = ({
  index,
  pricePeriod,
}: {
  index: number;
  pricePeriod?: SpotPricePeriod;
}) => {
  const time = useMemo(() => {
    return format(
      addMinutes(
        startOfDay(new Date()),
        pricePeriod === SpotPricePeriod['15M'] ? index * 15 : index * 60
      ),
      'HH:mm'
    );
  }, [index, pricePeriod]);

  return <>{time}</>;
};

export const PlugSummary15Min = ({ plugIndex }: Props) => {
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const sm = useMediaQuery(theme.breakpoints.up('sm'));

  const headSx: SxProps = {
    gridColumn: { xs: 'span 96', lg: 'auto' },
    fontSize: { xs: 12, lg: 14 },
    lineHeight: { xs: '12px', lg: '24px' },
    mt: { xs: 1.25, lg: 0 },
    mb: { xs: 0.5, lg: 0 },
    pr: { xs: 0, lg: 1 },
    width: 'auto',
  };

  const [
    prices,
    priceLevels15M,
    spotEnabled,
    spotLow15M,
    spotMid15M,
    spotHi15M,
    spotNeg15M,
    spotBelow15M,
    spotAbove15M,
    priceBelowValue15M,
    priceAboveValue15M,
    plugPricePeriod,
    spPricePeriod,
    spotEm,
    spotOverflow15M,
  ] = useSelector(
    ({
      variables: {
        SP_15M_PRICE,
        SP_PRICE_15M_LEVEL,
        PL_SPOT,
        PL_SPOT_LOW_15MIN,
        PL_SPOT_MI_15MIN,
        PL_SPOT_HI_15MIN,
        PL_SPOT_NEG_15MIN,
        PL_SPOT_BELOW_PLUG_15MIN,
        PL_SPOT_ABOVE_PLUG_15MIN,
        SP_PRICE_BELOW_PLUG_15MIN_VALUE,
        SP_PRICE_ABOVE_PLUG_15MIN_VALUE,
        PL_SP_PRICE_PERIOD,
        SP_PRICE_PERIOD,
        SP_EM,
        SP_OVERFLOW_15M_TABLE,
      },
    }) =>
      [
        SP_15M_PRICE,
        SP_PRICE_15M_LEVEL,
        PL_SPOT,
        PL_SPOT_LOW_15MIN,
        PL_SPOT_MI_15MIN,
        PL_SPOT_HI_15MIN,
        PL_SPOT_NEG_15MIN,
        PL_SPOT_BELOW_PLUG_15MIN,
        PL_SPOT_ABOVE_PLUG_15MIN,
        SP_PRICE_BELOW_PLUG_15MIN_VALUE,
        SP_PRICE_ABOVE_PLUG_15MIN_VALUE,
        PL_SP_PRICE_PERIOD[plugIndex],
        SP_PRICE_PERIOD,
        SP_EM,
        SP_OVERFLOW_15M_TABLE,
      ] as const
  );

  // Simple plan for 15M spot control for selected plug
  const schedule15M = useMemo(() => {
    const result: boolean[] = [];
    const isSpotOn = spotEnabled[plugIndex];

    for (let index = 0; index < 96; index++) {
      let on = false;
      if (!isSpotOn) {
        result.push(false);
        continue;
      }

      const level = priceLevels15M[index];
      const price = prices[index];

      if (spotLow15M[plugIndex] && level === SpotPriceLevel.Low) on = true;
      if (spotMid15M[plugIndex] && level === SpotPriceLevel.Mid) on = true;
      if (spotHi15M[plugIndex] && level === SpotPriceLevel.Hi) on = true;

      // Custom below / above price
      if (
        spotBelow15M[plugIndex] &&
        priceBelowValue15M[plugIndex] != null &&
        price <= priceBelowValue15M[plugIndex]
      ) {
        on = true;
      }
      if (
        spotAbove15M[plugIndex] &&
        priceAboveValue15M[plugIndex] != null &&
        price >= priceAboveValue15M[plugIndex]
      ) {
        on = true;
      }

      // Negative price – follow overflow 15m table across the day
      if (
        spotNeg15M[plugIndex] &&
        Array.isArray(spotOverflow15M) &&
        !spotOverflow15M[index]
      ) {
        on = true;
      }

      result.push(on);
    }

    return result;
  }, [
    plugIndex,
    prices,
    priceLevels15M,
    spotEnabled,
    spotLow15M,
    spotMid15M,
    spotHi15M,
    spotNeg15M,
    spotBelow15M,
    spotAbove15M,
    priceBelowValue15M,
    priceAboveValue15M,
    spotEm,
    spotOverflow15M,
  ]);

  if (
    plugPricePeriod !== SpotPricePeriod['15M'] &&
    spPricePeriod !== SpotPricePeriod['15M']
  )
    return null;
  if (!spotEnabled[plugIndex]) return null;

  return (
    <Paper sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(96, 1fr)',
            lg: 'auto repeat(96, 1fr)',
          },
          rowGap: { lg: 1 },
        }}
      >
        {/* Čas */}
        <Box sx={{ ...headSx }}>
          <FormattedMessage id="plug.summary.time" defaultMessage="Čas" />
        </Box>
        {summaryHead.map((i) => {
          if (!lg && i % 2 === 0) return null;
          if (!sm && i % 3 === 0) return null;

          return (
            <Box
              component="span"
              key={i}
              sx={{
                textAlign: 'left',
                fontSize: 12,
                gridColumn: { xs: 'span 12', sm: 'span 8', lg: 'span 4' },
                width: 'auto',
              }}
            >
              {i - 1}:00
            </Box>
          );
        })}

        {/* Hladina ceny 15m */}
        <Row sx={{ ...headSx }}>
          <FormattedMessage
            id="plug.summary.price_level_15m"
            defaultMessage="Hladina ceny 15m"
          />
        </Row>
        {priceLevels15M.map((value, i) => (
          <ColPriceLevel
            value={value}
            key={i}
            periodIndex={i}
            pricePeriod={SpotPricePeriod['15M']}
          />
        ))}

        {/* Sepnutí zásuvky */}
        <RowHead sx={headSx}>
          <FormattedMessage
            id="plug.summary.switch"
            defaultMessage="Sepnutí zásuvky"
          />
        </RowHead>
        {schedule15M.map((value, i) => (
          <ColBoolean
            key={i}
            value={value}
            periodIndex={i}
            pricePeriod={SpotPricePeriod['15M']}
          />
        ))}
      </Box>
    </Paper>
  );
};
